// src/components/AddSongModal.jsx
// Shown when artist adds a song to their master playlist.
// Collects title + artist (if manual add) and optionally a YouTube backing track URL.

import { useState } from 'react';
import { db } from '../firebase-utils';
import {
  collection, getDocs, query, orderBy,
  addDoc, updateDoc, doc, serverTimestamp
} from 'firebase/firestore';

const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) { const m = url.match(p); if (m) return m[1]; }
  return null;
};

// Upsert to Firestore song bank
export const upsertToSongBank = async (title, artist, youtubeId) => {
  try {
    const snap = await getDocs(query(collection(db, 'backingTracks'), orderBy('title')));
    const existing = snap.docs.find(d => {
      const t = d.data();
      return (
        t.title?.toLowerCase() === title.toLowerCase() &&
        t.artist?.toLowerCase() === artist.toLowerCase()
      );
    });
    if (existing) {
      if (!existing.data().youtubeId && youtubeId) {
        await updateDoc(doc(db, 'backingTracks', existing.id), {
          youtubeId,
          updatedAt: serverTimestamp(),
        });
      } else if (youtubeId && existing.data().youtubeId !== youtubeId) {
        // Artist is explicitly providing a new link — update it
        await updateDoc(doc(db, 'backingTracks', existing.id), {
          youtubeId,
          updatedAt: serverTimestamp(),
        });
      }
    } else {
      await addDoc(collection(db, 'backingTracks'), {
        title: title.trim(),
        artist: artist.trim(),
        genre: null,
        youtubeId: youtubeId || null,
        addedByArtist: true,
        addedAt: serverTimestamp(),
      });
    }
  } catch (e) {
    console.warn('Could not upsert to song bank:', e.message);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Props:
//   mode: 'manual' | 'itunes'
//   song: null (manual) | { title, artist, album, duration, ... } (itunes)
//   onConfirm(song) — called with the final song object to add
//   onClose()
// ─────────────────────────────────────────────────────────────────────────────
export default function AddSongModal({ mode, song, onConfirm, onClose }) {
  const [title, setTitle] = useState(song?.title || '');
  const [artist, setArtist] = useState(song?.artist || '');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const ytId = extractYouTubeId(youtubeUrl);
  const hasYoutube = !!ytId;
  const songTitle = mode === 'itunes' ? song.title : title;
  const songArtist = mode === 'itunes' ? song.artist : artist;

  const handleConfirm = async () => {
    setError('');
    if (!songTitle.trim()) { setError('Song title is required.'); return; }
    if (!songArtist.trim()) { setError('Artist name is required.'); return; }

    setSaving(true);

    const newSong = mode === 'itunes'
      ? { id: Math.floor(song.id), title: song.title, artist: song.artist, duration: song.duration, album: song.album || '', key: '' }
      : { id: Date.now(), title: title.trim(), artist: artist.trim(), duration: '', key: '' };

    await upsertToSongBank(newSong.title, newSong.artist, ytId || null);

    onConfirm(newSong);
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.88)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden flex flex-col"
        style={{ background: 'linear-gradient(135deg,#1a0a2e,#0d1a3a)', border: '1px solid rgba(0,245,255,0.25)' }}
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <div className="text-white font-black text-base">
              {mode === 'itunes' ? '🎵 Add to Master Playlist' : '🎵 New Song'}
            </div>
            {mode === 'itunes' && (
              <div className="text-cyan-400 text-xs mt-0.5 truncate max-w-xs">
                {song.title} — {song.artist}
              </div>
            )}
          </div>
          <button onClick={onClose}
            className="text-gray-400 hover:text-white text-xl font-bold w-8 h-8 flex items-center justify-center">✕</button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto" style={{ maxHeight: '70vh' }}>

          {/* iTunes song info card */}
          {mode === 'itunes' && song.artwork && (
            <div className="flex items-center gap-3 p-3 rounded-xl"
              style={{ background: 'rgba(0,245,255,0.06)', border: '1px solid rgba(0,245,255,0.15)' }}>
              <img src={song.artwork} alt={song.title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-white font-bold text-sm truncate">{song.title}</div>
                <div className="text-cyan-400 text-xs truncate">{song.artist}</div>
                {song.album && <div className="text-gray-400 text-xs truncate">{song.album}</div>}
                {song.duration && <div className="text-gray-500 text-xs">⏱ {song.duration}</div>}
              </div>
            </div>
          )}

          {/* Manual mode: title + artist fields */}
          {mode === 'manual' && (
            <>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Song Title *</label>
                <input value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Hotel California" autoFocus
                  className="w-full rounded-lg px-3 py-2 text-sm text-white"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', outline: 'none' }} />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Artist *</label>
                <input value={artist} onChange={e => setArtist(e.target.value)}
                  placeholder="e.g. Eagles"
                  className="w-full rounded-lg px-3 py-2 text-sm text-white"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', outline: 'none' }} />
              </div>
            </>
          )}

          {/* ── BACKING TRACK SECTION — shown for BOTH modes ── */}
          <div className="rounded-xl p-3 space-y-2"
            style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)' }}>
            <div className="flex items-center gap-2 mb-1">
              <span style={{ color: '#a855f7' }}>🎵</span>
              <span className="text-white font-bold text-xs">Backing Track</span>
              <span className="text-gray-500 text-xs">(optional)</span>
            </div>

            <p className="text-gray-400 text-xs leading-relaxed">
              Search <span className="text-purple-300 font-semibold">"{songTitle || 'song name'} karaoke"</span> on YouTube and paste the link below.
              Use karaoke/lyric videos — they rarely block playback.
            </p>

            <input
              value={youtubeUrl}
              onChange={e => setYoutubeUrl(e.target.value)}
              placeholder="Paste YouTube URL or video ID..."
              className="w-full rounded-lg px-3 py-2 text-sm text-white"
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: `1px solid ${hasYoutube ? 'rgba(168,85,247,0.6)' : youtubeUrl ? 'rgba(249,115,22,0.5)' : 'rgba(124,58,237,0.3)'}`,
                outline: 'none'
              }}
            />

            {hasYoutube && (
              <p className="text-purple-300 text-xs font-semibold">✓ Valid YouTube link detected</p>
            )}
            {youtubeUrl && !hasYoutube && (
              <p className="text-orange-400 text-xs">⚠ Doesn't look like a YouTube URL — will be skipped</p>
            )}

            {/* YouTube preview */}
            {hasYoutube && (
              <div className="rounded-lg overflow-hidden mt-2" style={{ aspectRatio: '16/9' }}>
                <iframe
                  width="100%" height="100%"
                  src={`https://www.youtube.com/embed/${ytId}`}
                  allow="encrypted-media"
                  allowFullScreen
                  style={{ border: 'none', display: 'block' }}
                />
              </div>
            )}

            {hasYoutube && (
              <div className="p-2 rounded-lg text-xs flex items-start gap-2"
                style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', color: '#c4b5fd' }}>
                🎸 This backing track will be saved to the GigWave Song Bank — available during your live gigs.
              </div>
            )}
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 flex gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-300"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-black text-black transition-all"
            style={{ background: saving ? '#555' : '#00f5ff', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : '+ Add to Playlist'}
          </button>
        </div>
      </div>
    </div>
  );
}
